import { CountBadge } from '@vrplatform/components';
import {
  type ResponseData,
  useApiQuery,
  useDashboard,
  useTeam,
} from '@vrplatform/data';
import { VRP_TENANT_ID } from '@vrplatform/graphql';
import type { ComponentProps } from 'react';
import { wherePartnerConnectionErrors } from './useWherePartnerConnectionErrors';
import { sum } from '@vrplatform/utils';

type Props = {
  type?: 'errors' | 'outdated';
};

type IssueCode =
  ResponseData<'get:/teams/issues'>['data'][number]['issues'][number]['code'];

const usePartnerConnectionErrorsAggregateQuery = ({ type }: Props) => {
  const [{ id: teamId }] = useTeam();
  const [dashboard] = useDashboard();

  return useApiQuery(
    async (api, signal, { teamId, dashboard, type }, hasura) => {
      const [errors, outdated] = await Promise.all([
        hasura(
          (q) => {
            const where = wherePartnerConnectionErrors({
              appId: undefined,
              partnerId: teamId === VRP_TENANT_ID ? undefined : teamId,
              search: undefined,
              tenantId: undefined,
              dashboard,
              status: 'active',
            });

            return q.connectionAggregate({ where }).aggregate?.count() || 0;
          },
          {
            signal,
          }
        ),
        api
          .GET('/teams/issues', {
            signal,
            params: {
              query: {
                codes: (['outdatedConnections'] satisfies IssueCode[]).join(
                  ','
                ),
              },
            },
          })
          .then((result) => {
            if (result.error) throw result.error;

            const aggregates = result.data.data.map(
              (row) =>
                (
                  row.issues.find((x) => x.code === 'outdatedConnections')
                    ?.context.connections ?? []
                ).length
            );
            return sum(aggregates);
          }),
      ]);

      if (type === 'outdated') {
        return outdated;
      }

      if (type === 'errors') {
        return errors;
      }

      return errors + outdated;
    },
    {
      queryGroup: 'connections',
      uniqueQueryKey: 'partner-connection-errors-aggregate',
      variables: {
        teamId,
        dashboard,
        type,
      },
    }
  );
};

export const PartnerConnectionErrorAggregateBadge = (
  props: Props & {
    loadingColor?: ComponentProps<typeof CountBadge>['loadingColor'];
  }
) => {
  const { data, isInitialLoading } =
    usePartnerConnectionErrorsAggregateQuery(props);

  return (
    (!!data || isInitialLoading) && (
      <CountBadge
        count={data || 0}
        loading={isInitialLoading}
        loadingColor={props.loadingColor}
      />
    )
  );
};
